<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue';
import { useTranslation } from 'i18next-vue';
import { useFeedStore } from '@/client/stores/feedStore';
import { useFeedFollows } from '@/client/composables/useFeedFollows';
import { useFeedEvents } from '@/client/composables/useFeedEvents';
import EmptyLayout from '@/client/components/common/empty_state.vue';
import FollowListItem from './follow_list_item.vue';
import AddCalendarModal from './add_calendar_modal.vue';

const { t } = useTranslation('feed', {
  keyPrefix: 'follows',
});

const feedStore = useFeedStore();
const { isLoading, loadFollows, unfollowCalendar, updateFollowPolicy } = useFeedFollows();
const { loadFeed } = useFeedEvents();
const showAddModal = ref(false);
const addModalTriggerRef = ref<HTMLElement | null>(null);

const follows = computed(() => feedStore.follows);

const handlePolicyChange = async (followId: string, autoRepostOriginals: boolean, autoRepostReposts: boolean) => {
  try {
    await updateFollowPolicy(followId, autoRepostOriginals, autoRepostReposts);
  }
  catch (error) {
    console.error('Error updating follow policy:', error);
  }
};

const handleUnfollow = async (followId: string) => {
  try {
    await unfollowCalendar(followId);
  }
  catch (error) {
    console.error('Error unfollowing calendar:', error);
  }
};

const handleOpenAddModal = (event: MouseEvent) => {
  addModalTriggerRef.value = (event?.currentTarget as HTMLElement) ?? null;
  showAddModal.value = true;
};

const handleCloseAddModal = () => {
  showAddModal.value = false;
  nextTick(() => { addModalTriggerRef.value?.focus(); });
};

// The follow dialog submits against the selected calendar, so one opened on
// the previous calendar closes when the selection changes. The selection
// follows the route's :calendar; this is the twin of feed/root.vue's watch.
watch(() => feedStore.selectedCalendarId, () => {
  showAddModal.value = false;
});

const handleFollowSuccess = async () => {
  // Refresh both follows list and feed events after successful follow
  // so the Events tab immediately shows events from the newly followed calendar.
  try {
    await Promise.all([loadFollows(), loadFeed()]);
  }
  catch (error) {
    console.error('Error refreshing feed after follow:', error);
  }
};
</script>

<template>
  <section v-if="isLoading" class="loading">
    {{ t('loading') }}
  </section>
  <section v-else-if="follows.length" class="follows-list">
    <div class="follows-header">
      <h2>{{ t('title') }}</h2>
      <button
        type="button"
        class="btn btn--primary add-button"
        @click="handleOpenAddModal"
      >
        {{ t('follow_button') }}
      </button>
    </div>

    <div class="follows-items">
      <FollowListItem
        v-for="follow in follows"
        :key="follow.id"
        :follow="follow"
        @policy-change="handlePolicyChange"
        @unfollow="handleUnfollow"
      />
    </div>
  </section>
  <EmptyLayout
    v-else
    :title="t('no_follows')"
    :guide="{ slug: 'guides/calendar-owners/follow-and-repost', key: 'follow_repost' }"
    :guide-label="t('guide_link')"
  >
    <button
      type="button"
      class="btn btn--cta btn--lg"
      @click="handleOpenAddModal"
    >
      {{ t('follow_button') }}
    </button>
  </EmptyLayout>

  <AddCalendarModal
    v-if="showAddModal"
    @close="handleCloseAddModal"
    @follow-success="handleFollowSuccess"
  />
</template>

<style scoped lang="scss">
section.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: var(--pav-color-text-primary);
  font-style: italic;
}

section.follows-list {
  display: flex;
  flex-direction: column;
  gap: var(--pav-space-4);
  padding: var(--pav-space-4);

  div.follows-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--pav-space-3);

    h2 {
      margin: 0;
      font-size: 1.5rem;
      font-weight: var(--pav-font-weight-medium);
      color: var(--pav-color-text-primary);
    }

    button.add-button {
      padding: var(--pav-space-2) var(--pav-space-4);
      background: var(--pav-color-interactive-primary);
      color: white;
      border: none;
      border-radius: var(--pav-border-radius-pill);
      font-weight: var(--pav-font-weight-medium);
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: var(--pav-color-interactive-primary-hover);
      }

      &:focus {
        outline: 2px solid var(--pav-color-interactive-primary);
        outline-offset: 2px;
      }
    }
  }

  div.follows-items {
    display: flex;
    flex-direction: column;
    gap: var(--pav-space-3);
  }
}
</style>
